import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { firstName, hasFamilyBubbles, nodeMeta, ORBIT_COLORS, type NetworkNode, type NetworkView } from '@rhwb/types';
import { fetchMyNetwork, fetchNetworkByEmail, fetchNetworkNode, removeFamily, removeRecruit } from '../../lib/network';
import FamilySection from './FamilySection';
import NetworkPickerDialog from './NetworkPickerDialog';
import OrbitSvg from './OrbitSvg';
import { DISPLAY_FONT, profileCardSx, SectionHeader } from '../profile/profileUi';

// The card spans the page, so the Orbit gets room to grow with the network.
const MAX_GRAPH_WIDTH = 720;

interface AmazingNetworkCardProps {
  /**
   * Admin override mode: whose network to show, read-only. Null shows the
   * signed-in runner's own network, editable.
   */
  viewEmail: string | null;
}

/**
 * Amazing Network on the web profile — the same Orbit as the mobile app: who
 * brought you to RHWB above, who you brought around you. Click anyone to
 * re-centre and keep walking the club tree; the trail steps back.
 */
const AmazingNetworkCard: React.FC<AmazingNetworkCardProps> = ({ viewEmail }) => {
  const readOnly = !!viewEmail;
  const [root, setRoot] = useState<NetworkView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The runners clicked through, in order; empty = centred on the root.
  const [trail, setTrail] = useState<NetworkView[]>([]);
  const [focusLoading, setFocusLoading] = useState(false);
  const [focusError, setFocusError] = useState<string | null>(null);
  const [picker, setPicker] = useState<'referrer' | 'recruit' | 'family' | null>(null);
  // Who is being removed, and from what: your recruits or your family.
  const [confirmRemove, setConfirmRemove] = useState<{ node: NetworkNode; kind: 'recruit' | 'family' } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // The graph fills the card up to MAX_GRAPH_WIDTH.
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.min(el.clientWidth, MAX_GRAPH_WIDTH));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [root]);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setRoot(await (viewEmail ? fetchNetworkByEmail(viewEmail) : fetchMyNetwork()));
      setTrail([]);
    } catch (err) {
      setLoadError((err as Error).message);
    }
  }, [viewEmail]);

  useEffect(() => {
    setRoot(null);
    load();
  }, [load]);

  const focus = trail[trail.length - 1] ?? null;
  const view = focus ?? root;
  const atRoot = !focus;
  const editable = atRoot && !readOnly && !!root?.me;
  const rootName = readOnly ? firstName(root?.me?.name) || 'Runner' : 'You';

  const openNode = async (node: NetworkNode) => {
    setFocusError(null);
    if (node.id === root?.me?.id) {
      setTrail([]);
      return;
    }
    const seen = trail.findIndex((t) => t.me?.id === node.id);
    if (seen >= 0) {
      setTrail(trail.slice(0, seen + 1));
      return;
    }
    if (node.id === view?.me?.id) return;
    setFocusLoading(true);
    try {
      const next = await fetchNetworkNode(node.id);
      setTrail((prev) => [...prev, next]);
    } catch (err) {
      setFocusError((err as Error).message);
    } finally {
      setFocusLoading(false);
    }
  };

  const onSaved = (next: NetworkView) => {
    setRoot(next);
    setTrail([]);
  };

  const doRemove = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      const { node, kind } = confirmRemove;
      onSaved(await (kind === 'family' ? removeFamily(node.id) : removeRecruit(node.id)));
      setConfirmRemove(null);
    } catch (err) {
      setRemoveError((err as Error).message);
    } finally {
      setRemoving(false);
    }
  };

  // A login without a runner profile (coach-only) has no network to show.
  if (root && !root.me && !readOnly) return null;

  const referrer = root?.ancestors[0] ?? null;
  const outsideReferrer = referrer ? null : root?.outsideReferrer ?? null;
  const foundSelf = !referrer && !outsideReferrer && !!root?.foundSelf;
  const focusName = focus?.me?.name ?? '';

  return (
    <Card sx={profileCardSx}>
      <Box sx={{ mb: 2.5 }}>
        <SectionHeader
          icon={<HubOutlinedIcon />}
          tint="#E3F6F3"
          color={ORBIT_COLORS.overflowText}
          title="Amazing Network"
          subtitle="How RHWB grew, one friend at a time"
          action={
            root && view ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', width: { xs: '100%', sm: 'auto' } }}>
                <strong style={{ color: '#16181D' }}>{view.stats.clubTotal}</strong>{' '}
                {view.stats.clubTotal === 1 ? 'runner has' : 'runners have'} joined RHWB through a friend
              </Typography>
            ) : undefined
          }
        />
      </Box>

      {loadError ? (
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {loadError}
        </Alert>
      ) : !root || !view ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2.5, md: 4 }, alignItems: { md: 'stretch' } }}>
          {/* Graph */}
          <Box
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              borderRadius: '16px',
              bgcolor: { md: '#FAFBFC' },
              border: { md: '1px solid #EEF0F3' },
              p: { md: 2 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {!atRoot && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => setTrail(trail.slice(0, -1))} aria-label="Back one level">
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                    {focusName}'s branch
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setTrail([])}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999, bgcolor: 'rgba(24,119,242,0.08)' }}
                  >
                    Back to {readOnly ? rootName : 'you'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', pl: 5 }}>
                  <Crumb label={rootName} onClick={() => setTrail([])} />
                  {trail.map((t, i) => (
                    <React.Fragment key={t.me?.id ?? i}>
                      <Typography component="span" sx={{ color: 'text.disabled', fontSize: 13 }}>
                        ›
                      </Typography>
                      <Crumb
                        label={firstName(t.me?.name)}
                        current={i === trail.length - 1}
                        onClick={() => setTrail(trail.slice(0, i + 1))}
                      />
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            )}

            <Box ref={boxRef} sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              {width > 0 && (
                <OrbitSvg
                  key={view.me?.id ?? 'root'}
                  view={view}
                  width={width}
                  // "You" draws the word; any other name draws its initials.
                  centerLabel={atRoot ? (readOnly ? root.me?.name ?? rootName : rootName) : focusName}
                  showReferrerSlot={editable}
                  showAddSlot={editable}
                  onNodeClick={openNode}
                  onSlotClick={editable ? () => setPicker('referrer') : undefined}
                  onAddClick={() => setPicker('recruit')}
                />
              )}
              {focusLoading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)' }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>

            {hasFamilyBubbles(view) && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 0.5 }}>
                <Box sx={{ width: 16, height: 10, borderRadius: 5, bgcolor: ORBIT_COLORS.familyFill, border: `1px solid ${ORBIT_COLORS.familyStroke}` }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Family
                </Typography>
              </Box>
            )}
            {view.moreCount > 0 && (
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary' }}>
                +{view.moreCount} more further down the line
              </Typography>
            )}
            {focusError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {focusError}
              </Alert>
            )}
          </Box>

          {/* Details */}
          <Box sx={{ flex: { md: '0 0 320px' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              <Stat value={view.stats.broughtIn} label={atRoot ? (readOnly ? `${rootName} brought in` : 'you brought in') : `${firstName(focusName)} brought in`} />
              <Stat value={view.stats.branchSize} label={atRoot && !readOnly ? 'in your branch' : 'in their branch'} />
            </Box>

            {atRoot && root.me && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, borderRadius: '14px', bgcolor: '#F5F1FE', pl: 1.75, pr: 1, py: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: referrer ? ORBIT_COLORS.referrer : 'transparent',
                    border: referrer
                      ? 'none'
                      : foundSelf
                        ? `1.5px solid ${ORBIT_COLORS.referrer}`
                        : outsideReferrer
                        ? `1.5px solid ${ORBIT_COLORS.outside}`
                        : `1.5px dashed ${ORBIT_COLORS.referrer}`,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                  {referrer ? (
                    <>
                      Brought in by <strong>{referrer.name}</strong>
                    </>
                  ) : outsideReferrer ? (
                    <>
                      Brought in by <strong>{outsideReferrer}</strong>{' '}
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        (not in RHWB)
                      </Box>
                    </>
                  ) : foundSelf ? (
                    readOnly ? 'Found RHWB on their own' : 'You found RHWB yourself'
                  ) : readOnly ? (
                    'No referrer on record'
                  ) : (
                    'Who brought you to RHWB?'
                  )}
                </Typography>
                {!readOnly && (
                  <Button size="small" onClick={() => setPicker('referrer')} sx={{ textTransform: 'none', fontWeight: 700, color: '#5B21B6', bgcolor: 'white', borderRadius: 999, px: 1.5, '&:hover': { bgcolor: 'white' } }}>
                    {referrer || outsideReferrer || foundSelf ? 'Change' : 'Add'}
                  </Button>
                )}
              </Box>
            )}

            {atRoot && root.me && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>
                  {readOnly ? `People ${rootName} brought in` : 'People you brought in'}
                </Typography>
                {root.children.length === 0 && readOnly && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Nobody yet.
                  </Typography>
                )}
                {root.children.map((kid) => (
                  <Box key={kid.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => openNode(kid)}
                      onKeyDown={(e) => e.key === 'Enter' && openNode(kid)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0, cursor: 'pointer', py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' } }}
                    >
                      <Avatar src={kid.picture ?? undefined} sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: ORBIT_COLORS.child }}>
                        {kid.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {kid.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                          {[nodeMeta(kid), kid.childCount ? `brought in ${kid.childCount}` : null].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Box>
                    {!readOnly && (
                      <IconButton size="small" onClick={() => setConfirmRemove({ node: kid, kind: 'recruit' })} aria-label={`Remove ${kid.name} from people you brought in`} sx={{ bgcolor: '#eef1f5' }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {!readOnly && (
                  <Button
                    onClick={() => setPicker('recruit')}
                    startIcon={<Box component="span" sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>+</Box>}
                    sx={{ mt: 0.5, height: 44, borderRadius: '12px', border: `1.5px dashed ${ORBIT_COLORS.grandchild}`, textTransform: 'none', fontWeight: 700, color: ORBIT_COLORS.overflowText }}
                  >
                    Add someone you brought in
                  </Button>
                )}
              </Box>
            )}

            {atRoot && root.me && (
              <FamilySection
                family={root.family ?? []}
                readOnly={readOnly}
                rootName={rootName}
                onOpen={openNode}
                onAdd={() => setPicker('family')}
                onRemove={(node) => setConfirmRemove({ node, kind: 'family' })}
                onChanged={onSaved}
              />
            )}

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Click anyone in the orbit to walk their branch of the club tree.
            </Typography>
          </Box>
        </Box>
      )}

      {!readOnly && root?.me && (
        <NetworkPickerDialog
          open={picker !== null}
          mode={picker ?? 'referrer'}
          me={root.me}
          ancestors={root.ancestors}
          outsideReferrer={root.outsideReferrer}
          foundSelf={root.foundSelf}
          onClose={() => setPicker(null)}
          onSaved={onSaved}
        />
      )}

      <Dialog open={!!confirmRemove} onClose={() => !removing && setConfirmRemove(null)}>
        <DialogTitle>Remove {confirmRemove?.node.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {firstName(confirmRemove?.node.name)} will no longer show as{' '}
            {confirmRemove?.kind === 'family' ? 'your family' : 'someone you brought in'}.
          </DialogContentText>
          {removeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {removeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)} disabled={removing} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={doRemove} disabled={removing} color="error" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const Crumb: React.FC<{ label: string; current?: boolean; onClick: () => void }> = ({ label, current, onClick }) => (
  <Typography
    component="button"
    onClick={onClick}
    disabled={current}
    sx={{
      border: 0,
      background: 'none',
      p: 0,
      fontSize: 13,
      fontWeight: 600,
      color: current ? 'text.disabled' : '#1877F2',
      cursor: current ? 'default' : 'pointer',
    }}
  >
    {label}
  </Typography>
);

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#E9F8F5', borderRadius: '14px', px: 2, py: 1.5 }}>
    <Typography sx={{ fontFamily: DISPLAY_FONT, fontStretch: '85%', fontSize: 32, fontWeight: 800, color: ORBIT_COLORS.overflowText, lineHeight: 1.05 }}>{value}</Typography>
    <Typography variant="caption" sx={{ color: '#3F5F5A', fontSize: 13 }} noWrap component="div">
      {label}
    </Typography>
  </Box>
);

export default AmazingNetworkCard;
